/**
 * The preview's browser objects (part 15): the layout worker started from its bundle under the Trusted Types policy,
 * a drawn page's bitmap put on a canvas, and an element's size watched. The entry point passes them to `boot`.
 */
import { connect } from '@cockatrice/layout';
import type { Bitmap, WorkerPort } from '@cockatrice/layout';
import type { LayoutConnection } from './controller.ts';

/** The browser's `Worker` constructor, as far as starting the layout worker goes. */
export type WorkerConstructor = new (url: string, options: WorkerOptions) => WorkerPort & { terminate(): void };

/** The browser's `ResizeObserver` constructor, as far as watching a size goes. */
export type ObserverConstructor = new (callback: (entries: readonly { readonly contentRect: { readonly width: number; readonly height: number } }[]) => void) => {
  observe(element: Element): void;
  disconnect(): void;
};

/**
 * What starts a worker: the constructor, the app's one Trusted Types policy (`workerUrlFactory`) and the worker bundle's
 * URL.
 */
export interface WorkerSource<W = WorkerPort & { terminate(): void }> {
  readonly Worker: new (url: string, options: WorkerOptions) => W;
  readonly trusted: (url: string) => string;
  readonly url: string;
}

/**
 * T6, T7: a function starting a layout worker from its bundle (a module worker) and connecting to it. Terminating it
 * stops its client too, so every request still waiting on the worker rejects (W15) rather than waiting for good.
 */
export function layoutWorker(source: WorkerSource): (store: string) => LayoutConnection {
  return (store) => {
    // Part 36a, table BK10: named by the book store whose images it reads, which the worker opens by that name.
    const worker = new source.Worker(source.trusted(source.url), { type: 'module', name: store });
    const client = connect(worker);
    return {
      client,
      terminate: () => {
        worker.terminate();
        client.stop();
      },
    };
  };
}

/** Puts a page's bitmap on a canvas sized to it, through the canvas's bitmap renderer; anything else is ignored. */
export function presentBitmap(canvas: HTMLCanvasElement, bitmap: Bitmap): void {
  if (!(bitmap instanceof ImageBitmap)) return;
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('bitmaprenderer')?.transferFromImageBitmap(bitmap);
}

/**
 * Calls `changed` with `element`'s content width and height now and on every resize, through a `ResizeObserver`; returns
 * what stops it.
 */
export function watchSize(Observer: ObserverConstructor, element: HTMLElement, changed: (width: number, height: number) => void): () => void {
  changed(element.clientWidth, element.clientHeight);
  const observer = new Observer((entries) => {
    for (const entry of entries) changed(entry.contentRect.width, entry.contentRect.height);
  });
  observer.observe(element);
  return () => {
    observer.disconnect();
  };
}
